import { NOCD_CARE_TEAM_EMAIL } from "@core/constants";
import ContainedPageWithHeading from "@core/layout/ContainedPageWithHeading";
import Question from "@core/ui/Disclosure";
import { ReactNode } from "react";

const QUESTIONS: { question: string; answer: ReactNode }[] = [
  {
    question: "How much will I owe for my therapy sessions using my insurance?",
    answer: (
      <>
        Insurance coverage for virtual behavioral healthcare can sometimes be
        less clear than standard medical coverage. Some plans have rules they
        don&apos;t report to us when we check your eligibility, but we always
        want you to know what you&apos;ll owe.
        <br />
        <br />
        On our end, we bill a set rate based on the length and type of your
        sessions, but the billed amount is not what you&apos;re expected to pay.
        Insurance applies a contracted adjustment, and what you&apos;ll owe
        depends on your individual plan&apos;s details like deductibles,
        copayments, and coinsurance. If you have a deductible, you may need to
        pay out-of-pocket until it&apos;s met.
        <br />
        <br />
        Since these details can be complex, we encourage you to call your
        insurance directly to ask if your behavioral health benefits apply to
        your deductible - or if you only have a copay for sessions. When
        calling, please mention these details:
        <br />
        <br />
        • Provider name: NOCD Inc.
        <br />
        • Place of service: 02 (telehealth)
        <br />
        <br />
        You can also get an estimate by sharing these CPT codes which describe
        the type and length of your sessions:
        <br />
        <br />
        • 90791 &ndash; Initial session <br />
        • 90837 &ndash; 60-minute session <br />
        • 90834 &ndash; 45-minute session <br />
        • 90832 &ndash; 30-minute session
        <br />
        <br />
        <b>
          <u>United Healthcare</u>
        </b>
        <br />
        Services billed through plans administered by United Healthcare are
        submitted in weekly bundles as follows:
        <br />
        <br />
        • H2020: Used for the first four weeks of therapist sessions
        <br />
        • H2020 U1: Used after the first four weeks for two or more therapy
        sessions per week
        <br />
        • H2020 U2: Used after the first four weeks for one session per week
        <br />
        <br />
        <b>
          <u>Horizon Blue Cross Blue Shield</u>
        </b>
        <br />
        Services billed through Horizon Blue Cross Blue Shield are submitted in
        weekly bundles as follows:
        <br />
        <br />
        • 909791: Initial session
        <br />
        • H0046 V1: First two months of treatment
        <br />
        • H0046 V2: Two or less therapy sessions per month, month three onward
        <br />
        • H0046 V3: Three or more therapy sessions per month, month three onward
        <br />
        <br />
        We strive to be transparent about billing, especially when it comes to
        helping you understand your estimated costs for care. If you have any
        questions, our team is here to help.
      </>
    ),
  },
  {
    question:
      "Why do I have to share my credit card information if you are billing my insurance?",
    answer: (
      <>
        As in many other industries, we must have a credit card on file at the
        start of service. Nothing is charged to the card immediately. The reason
        we initially ask for a credit or debit card is to schedule and save the
        session you are booking with us. Cards on file are used for any
        cancellation fees or balances associated with your sessions after we
        have billed your insurance. We&apos;ll always email you ahead of time to
        let you know of any upcoming charges, so you have time to reach out if
        you&apos;d like to discuss or set up different payment options.
      </>
    ),
  },
  {
    question: "When and how will I receive my first bill?",
    answer: (
      <>
        Invoices start to get generated as soon as you complete a session, but
        please know if you are using insurance, it can sometimes take up to 4-6
        weeks for a session to be processed under certain plans. You will always
        receive an email from us with any amount you&apos;ll owe, such as a
        copay, before you are charged. If you&apos;re paying out of pocket for
        sessions, you&apos;ll receive your invoices shortly after completing
        each session. If you decide to shift to a payment plan at any point in
        your journey, you will continue to get email notifications whenever a
        new invoice is generated, but you won&apos;t be charged until Fridays.
      </>
    ),
  },

  {
    question:
      "I am getting billed multiple copays at once. Do I have to pay them all together?",
    answer: (
      <>
        This can happen based on the way certain insurance companies process
        claims. When it does, you always have the option to go on one of our
        interest-free payment plans if paying all at once does not work for you.
        We offer two payment plans with no interest or hidden fees. You can
        choose to pay a predictable amount of $95 per week or $50 per week and
        slowly pay off your balance over time like buying a new computer. To
        enroll in either payment plan option, please email billing@nocdhelp.com
        and they will get you set up in no time! While it wouldn&apos;t lower
        the cost of your sessions, it would allow you to get immediate help and
        then pay off your balance over time, so you can get the help you need
        and deserve now. It also helps you avoid having a large outstanding
        balance at the end of treatment.
      </>
    ),
  },
  {
    question:
      "My therapist wants me to have more sessions and I'm not sure how the costs will add up. Can you offer any support?",
    answer: (
      <>
        We trust that your therapist is recommending what&apos;s best for your
        treatment plan, and we want to support you in this journey as best as we
        can. What you can do proactively is call your insurance with our CPT
        codes based on session lengths to get an early sense of combined costs.
        You always have the option to go on one of our interest free payment
        plans. We offer two payment plans with no interest or hidden fees. You
        can choose to pay a predictable amount of $95 per week or $50 per week
        and slowly pay off your balance over time like buying a new computer. To
        enroll in either payment plan option, please email billing@nocdhelp.com
        and they will get you set up in no time! While it wouldn&apos;t lower
        the cost of your sessions, it would allow you to get immediate help and
        then pay off your balance over time, so you can get the help you need
        and deserve now. It also helps you avoid having a large outstanding
        balance at the end of treatment.
      </>
    ),
  },
  {
    question:
      "If I receive an explanation of benefits (EOB) from my insurance and see an owed amount on my NOCD account, who do I pay, NOCD or my insurance?",
    answer: (
      <>
        Any EOB document you receive from your provider is just to give you an
        overview of what you&apos;ll owe, if anything, to NOCD. Payments should
        only be made to us, not your insurance provider. Please always wait for
        a NOCD invoice and go by what&apos;s posted in your NOCD account before
        submitting any sort of payment.
      </>
    ),
  },
  {
    question:
      "I need an invoice for a session I've already had. How do I get one?",
    answer: (
      <>
        Anytime you need a copy of an invoice - recent or from a few months
        back, you can download it right from the “Invoice” section of your
        billing page in your NOCD account. If you get stuck locating what you
        need, please reach out to us at{" "}
        <a href="mailto:care@nocdhelp.com" className="text-teal-600 ">
          care@nocdhelp.com
        </a>
      </>
    ),
  },
  {
    question:
      "My insurance is in-network, but my invoices are showing costs I am not familiar with. Can you explain?",
    answer: (
      <>
        The amount showing as billed on your invoice to insurance is not the
        amount we expect you to be paying. This is a &quot;bill rate.&quot; You
        will also see an &quot;insurance adjusted&quot; amount, which is
        reflective of what is being discounted or waived by your insurance. The
        amount you owe is based on your individual health plan and NOCD&apos;s
        discounted, contracted rate.
        <br />
        <br />
        While our standard cash pay rate is $240 per session, using insurance
        involves a different pricing structure. The billed rate to insurance
        will always be higher than our cash pay rates, but we do not expect to
        be paid what we bill to insurance. The amount you&apos;re responsible
        for paying out of pocket, as indicated on your invoice, is determined by
        your plan&apos;s coverage details, such as deductibles, copayments, and
        coinsurance. Depending on your plan, you may have different cost-sharing
        responsibilities that impact the final amount you owe.
      </>
    ),
  },
  {
    question: "I was billed for a canceled session. How do I reconcile this?",
    answer: (
      <>
        If you were billed for a session that you canceled or were not able to
        attend, it is likely because it fell under our late cancellation policy.
        Our policy is that members must let us know at least 48 hours before a
        session if they need to cancel or reschedule to avoid a late
        cancellation fee ($100 for a 60-min session, $75 for a 45-min session,
        $50 for a 30-min session). We know things come up, and we strive to make
        the situation right, but we are dedicated to providing help to as many
        members as possible which means requiring advance notice to book members
        if spots become available. NOCD&apos;s cancellation policy is included
        in the consent form, Good Faith Estimate (GFE), and payment agreement we
        ask every member to sign before treatment. If you feel there was an
        error in how you were invoiced and charged for a session, please reach
        out to us at{" "}
        <a href="mailto:care@nocdhelp.com" className="text-teal-600 ">
          care@nocdhelp.com
        </a>{" "}
        and we can look into it together.
      </>
    ),
  },
  {
    question:
      "What happens if I find out my therapist is out-of-network after we’ve had our first session together?",
    answer: (
      <>
        If you receive an explanation of benefits stating that your therapist is
        out-of- network, please reach out to us immediately so we can verify. We
        never want to burden you with any unanticipated costs. Sometimes, health
        plans process insurance claims in error, and we need to work with them
        to get things corrected. If it turns out your therapist is
        out-of-network, you will not be responsible for any sessions that took
        place and we&apos;ll work together to decide if you want to proceed as a
        cash pay member or transfer to an in-network therapist.
      </>
    ),
  },
  {
    question:
      "I need to submit a superbill to insurance for out-of-network reimbursement. How can I get a superbill quickly?",
    answer: (
      <>
        Superbills for sessions that have been paid in full can be found inside
        the billing section of your NOCD account. You can simply download one or
        as many as you like. If you need any help finding or downloading them,
        please reach out to us at{" "}
        <a href="mailto:care@nocdhelp.com" className="text-teal-600 ">
          care@nocdhelp.com
        </a>{" "}
        and we’ll walk you through it.
      </>
    ),
  },
  {
    question:
      "I have multiple insurance plans. Can I give you the plan I know is in-network with NOCD to bill my sessions to?",
    answer: (
      <>
        We absolutely need to be made aware of every possible insurance provider
        you are enrolled with so when we go to verify your eligibility, there is
        no confusion among the providers. This also helps us find your lowest
        cost coverage for your sessions. You can upload multiple insurance cards
        right on the billing page of your NOCD account.
      </>
    ),
  },
  {
    question:
      "Someone in my family handles the billing for my therapy. Can they receive my invoices directly?",
    answer: (
      <>
        All invoices are connected to the email address we have associated with
        your account. This email links your account to everything on our end and
        directly to your Member account on the web and your NOCD app. Whenever
        an invoice is ready, we&apos;ll use this email address to communicate
        with you about it. You will hear from us with any amount that is owed
        like a copay, ahead of when charges are actually due. In cases where
        someone else handles the billing, if you&apos;re comfortable, you can
        allow them to log into your Member account to pay invoices. If
        you&apos;re a parent or guardian and manage therapy for a minor, we are
        happy to set up a linked account or discuss specific requests for how
        you receive invoices. Please reach out to us at{" "}
        <a href="mailto:care@nocdhelp.com" className="text-teal-600 ">
          care@nocdhelp.com
        </a>{" "}
        for help.
      </>
    ),
  },
];

export function BillingFAQsScreen(): JSX.Element {
  return (
    <ContainedPageWithHeading title="Billing FAQs">
      <div className="mt-6 grid gap-y-4">
        {QUESTIONS.map((faq) => (
          <Question
            key={faq.question}
            heading={faq.question}
            body={faq.answer}
          />
        ))}
      </div>

      <p className="mb-3 mt-8 tablet:text-18px">
        We strive to be transparent about our billing practices and any charges
        you may receive for your care. If we can help you understand your
        invoice, coverage, or any of our policies in more detail, please feel
        free to get in touch.
      </p>

      <p className="pb-16 tablet:text-18px">
        Need more help? Please{" "}
        <a href={`mailto:${NOCD_CARE_TEAM_EMAIL}`} className="text-teal-600">
          contact us
        </a>
        .
      </p>
    </ContainedPageWithHeading>
  );
}
